'use client'

import { useState } from 'react'
import { useSession, signIn } from 'next-auth/react'
import { Button } from '@/components/ui/button'
import { CopyButton } from './CopyButton'
import { SyntaxPre } from './syntax-highlight'
import { cn } from '@/lib/utils'

const AUTH_HEADER = 'Authorization: Bearer YOUR_ACCESS_TOKEN'

function maskToken(token: string): string {
  return '•'.repeat(Math.min(token.length, 24))
}

export function AuthSection() {
  const { data: session, status } = useSession()
  const [visible, setVisible] = useState(false)

  const isLoading = status === 'loading'
  const expired = session?.error === 'RefreshAccessTokenError'
  const token = session?.accessToken
  const signedIn = status === 'authenticated' && !!token && !expired

  return (
    <section id='authentication' className='scroll-mt-28 space-y-8 border-b border-black/[0.06] pb-12'>
      <div>
        <h2 className='text-display-3 font-bold text-brand-navy'>Authentication</h2>
      </div>

      <div className='space-y-3 text-sm text-muted-foreground'>
        <p>Public endpoints require no authentication.</p>
        <p>Protected endpoints require a Bearer token in the Authorization header.</p>
      </div>

      <div className='space-y-2'>
        <p className='text-sm font-medium text-brand-navy'>Authorization header</p>
        <div className='flex items-center gap-2 rounded-xl bg-brand-cornflower/20 px-4 py-3 ring-1 ring-brand-cornflower/30'>
          <code className='flex-1 overflow-x-auto font-mono text-xs text-brand-navy'>{AUTH_HEADER}</code>
          <CopyButton value={AUTH_HEADER} />
        </div>
      </div>

      <div className='space-y-3'>
        <p className='text-sm font-medium text-brand-navy'>Your Bearer token</p>

        {isLoading ? (
          <div className='h-12 animate-shimmer rounded-xl bg-black/[0.06]' aria-busy='true' />
        ) : expired ? (
          <p className='text-sm text-muted-foreground'>Session expired. Please sign in again.</p>
        ) : !signedIn ? (
          <div className='space-y-3'>
            <p className='text-sm text-muted-foreground'>Sign in to view your token.</p>
            <Button
              variant='outline'
              size='sm'
              className='rounded-full'
              onClick={() => signIn('keycloak')}
            >
              Sign In
            </Button>
          </div>
        ) : (
          <div className='space-y-3'>
            <div className='flex items-center gap-2 rounded-xl bg-brand-cornflower/20 px-4 py-3 ring-1 ring-brand-cornflower/30'>
              <code
                className={cn(
                  'flex-1 overflow-x-auto font-mono text-xs text-brand-navy',
                  !visible && 'tracking-widest'
                )}
              >
                {visible ? token : maskToken(token!)}
              </code>
            </div>
            <div className='flex flex-wrap gap-2'>
              <Button
                type='button'
                variant='outline'
                size='sm'
                className='rounded-full'
                onClick={() => setVisible(true)}
                disabled={visible}
              >
                Show
              </Button>
              <Button
                type='button'
                variant='outline'
                size='sm'
                className='rounded-full'
                onClick={() => setVisible(false)}
                disabled={!visible}
              >
                Hide
              </Button>
              <CopyButton value={token!} label='Copy Token' size='default' />
            </div>
          </div>
        )}
      </div>
    </section>
  )
}
